import React, { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Extra security gate for /admin routes.
 *
 * Flow:
 *   1. On mount → GET /admin/otp/status
 *   2. If verified → render children immediately
 *   3. If not → show a full-screen challenge card:
 *      a) Click "Send code" → POST /admin/otp/request (email delivered to
 *         info.astrofate@gmail.com, hardcoded server-side)
 *      b) Enter 6-digit code → POST /admin/otp/verify
 *      c) On success → re-check status, remount children
 *
 * The gate is renewed every 30 minutes so a stolen session token alone
 * can't reach the admin dashboard.
 */
export default function AdminOTPGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [verified, setVerified] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [step, setStep] = useState("idle"); // idle | sent | verifying
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshStatus = () => {
    setChecking(true);
    axios.get(`${API}/admin/otp/status`, { withCredentials: true })
      .then(r => {
        setVerified(!!r.data?.verified);
        setRemaining(r.data?.remaining_seconds || 0);
      })
      .catch(() => setVerified(false))
      .finally(() => setChecking(false));
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const requestCode = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const { data } = await axios.post(`${API}/admin/otp/request`, {}, { withCredentials: true });
      setStep("sent");
      setInfo(data?.message || "OTP sent to info.astrofate@gmail.com. Check inbox.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not send OTP.");
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setErr(""); setInfo("");
    try {
      await axios.post(`${API}/admin/otp/verify`, { code: code.trim() }, { withCredentials: true });
      setCode("");
      refreshStatus();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Verification failed.");
    } finally { setBusy(false); }
  };

  if (checking) {
    return <div style={{ color: "#c4b5fd", padding: 40, textAlign: "center" }}>Verifying admin session…</div>;
  }

  if (verified) {
    const mins = Math.max(0, Math.floor(remaining / 60));
    return (
      <div>
        <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: "10px", padding: "8px 14px", marginBottom: "18px", color: "#86efac", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "8px" }}>
          <Shield size={14} /> Admin session verified · {mins > 0 ? `${mins} min` : "<1 min"} remaining
        </div>
        {children}
      </div>
    );
  }

  return (
    <div data-testid="admin-otp-gate" style={{ maxWidth: "460px", margin: "40px auto", padding: "32px", borderRadius: "18px", background: "linear-gradient(160deg,#1a0033,#120021)", border: "1px solid rgba(255,209,102,0.22)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <Shield size={22} style={{ color: "#FFD166" }} />
        <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166", margin: 0 }}>Admin Access Verification</h2>
      </div>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.86rem", lineHeight: 1.6 }}>
        For extra security, admin access requires a one-time code emailed to <strong style={{ color: "#FFD166" }}>info.astrofate@gmail.com</strong>. Code is valid for 10 minutes; verification lasts 30 minutes.
      </p>

      {step === "idle" && (
        <button data-testid="admin-otp-request-btn" disabled={busy} onClick={requestCode} className="btn-gold" style={{ marginTop: 16, width: "100%" }}>
          <Mail size={14} style={{ display: "inline", marginRight: 8 }} />
          {busy ? "Sending…" : "Email me a code"}
        </button>
      )}

      {step === "sent" && (
        <form onSubmit={verifyCode} style={{ marginTop: 16 }}>
          <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>Enter 6-digit code</label>
          <input data-testid="admin-otp-code-input" type="text" inputMode="numeric" autoFocus autoComplete="one-time-code"
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="cosmic-input" style={{ textAlign: "center", letterSpacing: "8px", fontSize: "1.4rem", fontFamily: "'JetBrains Mono', monospace" }}
            placeholder="000000" maxLength={6} />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button type="submit" data-testid="admin-otp-verify-btn" disabled={busy || code.length !== 6} className="btn-gold" style={{ flex: 1 }}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button type="button" disabled={busy} onClick={requestCode} className="btn-ghost">
              Resend
            </button>
          </div>
        </form>
      )}

      {info && <div style={{ marginTop: 12, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "8px", padding: "8px 12px", color: "#86efac", fontSize: "0.82rem" }}>{info}</div>}
      {err && <div data-testid="admin-otp-error" style={{ marginTop: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", padding: "8px 12px", color: "#fca5a5", fontSize: "0.82rem" }}>{err}</div>}
    </div>
  );
}
